// WhatsApps de fora do Venda no Zap na Central (Pedro, 03/11/2026: «esqueci meu telefone em casa, e tem 2 whatsapps que
// utilizo no dia a dia, mas que não estão associados ao Venda no Zap… inserir eles na página /atendimento. Mas de uma
// forma visualmente separada»). Aba «Pessoais», em violeta. As conversas e as mensagens vêm direto da UAZAPI (a API lê com
// o token, que nunca vem ao navegador). A conversa aberta é a mesma da Central, falando pelo canal do número (04/11:
// «Você esqueceu de copiar outras funções… que já existem na Central… Só é para deixar de fora o que for relacionado ao
// sistema do Venda no Zap»): áudio, mídia, etiquetas, lida e não lida, reação, apagar, responder, fixar e rascunho.
import { createContext, useEffect, useMemo, useRef, useState } from "react";
import { useQueries, useQuery, useQueryClient, type QueryKey } from "@tanstack/react-query";
import { customFetch } from "@workspace/api-client-react";
import { ChevronDown, Loader2, Pin, Search, Users, X } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useToast } from "@/hooks/use-toast";
import { extractApiErrorMessage } from "@/lib/api-error";

export interface Externo {
  id: number;
  rotulo: string;
  conectado: boolean;
}
export interface ConversaExterna {
  externo: number;
  chatid: string;
  nome: string;
  grupo: boolean;
  /** Negativo: marcada como não lida no celular (o WhatsApp mostra só a bolinha). */
  naoLidas: number;
  ultima: { texto: string; tipo: string; em: string | null };
  imagem: string | null;
  /** Os ids das etiquetas do WhatsApp do número. */
  etiquetas: string[];
  /** Fixada no topo da aba (só aqui, não no WhatsApp). */
  fixada: boolean;
}
interface EtiquetaExterna {
  id: string;
  nome: string;
  cor: string | null;
  conversas: number;
}

const BASE = "/api/admin/atendimento/externos";
// Uma cor por número, sempre na família do violeta (o Venda no Zap usa as cores dos Nº 1, 2 e 3).
const CORES = [
  "bg-violet-100 text-violet-800 dark:bg-violet-900/50 dark:text-violet-200",
  "bg-fuchsia-100 text-fuchsia-800 dark:bg-fuchsia-900/50 dark:text-fuchsia-200",
  "bg-indigo-100 text-indigo-800 dark:bg-indigo-900/50 dark:text-indigo-200",
];
const corDo = (externos: Externo[], id: number) => CORES[Math.max(0, externos.findIndex((e) => e.id === id)) % CORES.length]!;
const rotuloCurto = (r: string) => r.split(/\s+/)[0] ?? r;
const chaveDe = (c: { externo: number; chatid: string }) => `${c.externo}:${c.chatid}`;

/** Por onde a conversa aberta fala: os números do VnZ (/api/admin/atendimento) ou um WhatsApp pessoal, com as mesmas rotas
 *  debaixo de /externos/:id. */
export interface Canal {
  base: string;
  /** Prefixo das chaves guardadas no aparelho (rascunho, citação) e das consultas da conversa: a mesma pessoa no VnZ e num
   *  número pessoal são duas conversas. */
  chave: string;
  /** As consultas da lista e das etiquetas, para reler depois de uma mudança. */
  lista: QueryKey;
  etiquetas: QueryKey;
  /** Um WhatsApp pessoal: sem o que é do VnZ (Atendente, robozinho, To Do, loja e as referências para as sessões). */
  pessoal: boolean;
  /** O número, no topo da conversa (só no pessoal). */
  rotulo?: string;
  cor?: string;
}
export const CANAL_DO_VNZ: Canal = { base: "/api/admin/atendimento", chave: "", lista: ["atendimento-conversas"], etiquetas: ["atendimento-etiquetas"], pessoal: false };
export const CanalDaConversa = createContext<Canal>(CANAL_DO_VNZ);
const chaveDasEtiquetas = (id: number): QueryKey => ["atendimento-etiquetas", `ext${id}:`];
export function canalDoExterno(externos: Externo[], id: number): Canal {
  const e = externos.find((x) => x.id === id);
  return {
    base: `${BASE}/${id}`,
    chave: `ext${id}:`,
    lista: ["central-externo-conversas", id],
    etiquetas: chaveDasEtiquetas(id),
    pessoal: true,
    rotulo: e?.rotulo,
    cor: corDo(externos, id),
  };
}

/** O telefone da conversa como a Central mostra: +55 (DDD) número. */
export function telefoneDoJid(jid: string): string {
  const d = jid.split("@")[0]!.replace(/\D/g, "");
  if (!d.startsWith("55") || (d.length !== 12 && d.length !== 13)) return `+${d}`;
  const resto = d.slice(4);
  return `+55 (${d.slice(2, 4)}) ${resto.slice(0, resto.length - 4)}-${resto.slice(-4)}`;
}

export function useExternos() {
  return useQuery({
    queryKey: ["central-externos"],
    queryFn: () => customFetch<{ externos: Externo[] }>(BASE),
    staleTime: 60_000,
    refetchInterval: 120_000,
    retry: false,
  });
}

const ROTULO_DO_TIPO: Array<[RegExp, string]> = [
  [/image/i, "📷 Foto"],
  [/video/i, "🎬 Vídeo"],
  [/audio|ptt/i, "🎤 Áudio"],
  [/sticker/i, "Figurinha"],
  [/document/i, "📄 Documento"],
  [/location/i, "📍 Localização"],
  [/contact/i, "👤 Contato"],
  [/^call$/i, "📞 Chamada"],
];
const rotuloDoTipo = (tipo: string) => ROTULO_DO_TIPO.find(([r]) => r.test(tipo))?.[1] ?? null;

function hora(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  const hoje = new Date();
  return d.toDateString() === hoje.toDateString()
    ? d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })
    : d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });
}

/** A seleção «Venda no Zap | Pessoais» do topo da lista. */
export function SeletorDoModo({ modo, onMudar }: { modo: "vnz" | "pessoais"; onMudar: (m: "vnz" | "pessoais") => void }) {
  return (
    <div className="grid grid-cols-2 gap-1 rounded-lg bg-muted p-1 text-xs font-semibold" role="tablist" aria-label="Quais WhatsApps">
      <button
        type="button"
        role="tab"
        aria-selected={modo === "vnz"}
        onClick={() => onMudar("vnz")}
        className={`rounded-md py-1.5 ${modo === "vnz" ? "bg-background text-foreground shadow" : "text-muted-foreground hover:text-foreground"}`}
        data-testid="atendimento-modo-vnz"
      >
        Venda no Zap
      </button>
      <button
        type="button"
        role="tab"
        aria-selected={modo === "pessoais"}
        onClick={() => onMudar("pessoais")}
        className={`rounded-md py-1.5 ${modo === "pessoais" ? "bg-violet-600 text-white shadow" : "text-muted-foreground hover:text-foreground"}`}
        data-testid="atendimento-modo-pessoais"
      >
        Pessoais
      </button>
    </div>
  );
}

// Os filtros da aba voltam ao recarregar, como os da Central (a busca não).
const CHAVE_DOS_FILTROS = "central-pessoais-filtros";
function filtrosGuardados(): { numero: number | null; naoLidas: boolean; etiqueta: string | null } {
  try {
    const x = JSON.parse(localStorage.getItem(CHAVE_DOS_FILTROS) ?? "{}") as Record<string, unknown> | null;
    return {
      numero: typeof x?.numero === "number" ? x.numero : null,
      naoLidas: x?.naoLidas === true,
      etiqueta: typeof x?.etiqueta === "string" && /^\d+:\d+$/.test(x.etiqueta) ? x.etiqueta : null,
    };
  } catch {
    return { numero: null, naoLidas: false, etiqueta: null };
  }
}

const POR_VEZ = 60;
// A marcada (como não lida, ou fixada) aqui vale até a lista mostrar, ou 30 s.
const MARCADA_MAX_MS = 30_000;
type Filtros = { busca: string; naoLidas: boolean; etiqueta: string | null; limite: number };
type Pagina = { conversas: ConversaExterna[]; proximo: number | null; naoLidasTotal: number | null };

// As primeiras `limite` conversas do número (de 200 em 200, o máximo da UAZAPI por vez); as fixadas vêm no topo.
async function carregarConversas(id: number, f: Filtros): Promise<Pagina> {
  const conversas: ConversaExterna[] = [];
  let offset = 0;
  let proximo: number | null = null;
  let naoLidasTotal: number | null = null;
  for (;;) {
    const p = new URLSearchParams({ limite: String(Math.max(1, Math.min(200, f.limite - conversas.length))), offset: String(offset) });
    if (f.busca) p.set("busca", f.busca);
    if (f.naoLidas) p.set("nao_lidas", "1");
    if (f.etiqueta) p.set("etiqueta", f.etiqueta);
    const r = await customFetch<{ conversas: Omit<ConversaExterna, "externo">[]; proximo: number | null; nao_lidas_total: number | null }>(`${BASE}/${id}/conversas?${p}`);
    if (offset === 0) naoLidasTotal = r.nao_lidas_total;
    for (const c of r.conversas) conversas.push({ ...c, externo: id });
    proximo = r.proximo;
    if (proximo == null || conversas.length >= f.limite) break;
    offset = proximo;
  }
  return { conversas, proximo, naoLidasTotal };
}

/** As conversas dos WhatsApps pessoais, juntas e pela última mensagem, com a etiqueta do número: a busca, os filtros por
 *  número, «Não lidas» e etiqueta, e o menu do card (fixar, marcar como não lida), como na Central. */
export function ListaPessoais({
  externos,
  aberta,
  onAbrir,
  onAtualizar,
  rascunhoDe,
}: {
  externos: Externo[];
  aberta: ConversaExterna | null;
  /** null = fechar a aberta (marcada como não lida: abrir de novo marcaria como lida). */
  onAbrir: (c: ConversaExterna | null) => void;
  /** A versão nova da aberta, quando a lista relê (etiquetas, não lidas, fixada). */
  onAtualizar: (c: ConversaExterna) => void;
  rascunhoDe: (c: ConversaExterna) => string;
}) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [guardados] = useState(filtrosGuardados);
  const [numero, setNumero] = useState<number | null>(guardados.numero);
  const [naoLidas, setNaoLidas] = useState(guardados.naoLidas);
  // A etiqueta é «número:labelid»: cada WhatsApp tem as dele.
  const [etiqueta, setEtiqueta] = useState<string | null>(guardados.etiqueta);
  useEffect(() => {
    try {
      localStorage.setItem(CHAVE_DOS_FILTROS, JSON.stringify({ numero, naoLidas, etiqueta }));
    } catch {
      // sem armazenamento: os filtros valem até recarregar
    }
  }, [numero, naoLidas, etiqueta]);
  const [buscaDigitada, setBuscaDigitada] = useState("");
  const [busca, setBusca] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setBusca(buscaDigitada.trim()), 350);
    return () => clearTimeout(t);
  }, [buscaDigitada]);
  const [limite, setLimite] = useState(POR_VEZ);
  // Filtro novo começa do começo.
  useEffect(() => setLimite(POR_VEZ), [numero, naoLidas, etiqueta, busca]);
  // Número que saiu da lista (ou etiqueta de um número que saiu) deixa de filtrar.
  useEffect(() => {
    if (numero != null && !externos.some((e) => e.id === numero)) setNumero(null);
    if (etiqueta && !externos.some((e) => e.id === Number(etiqueta.split(":")[0]))) setEtiqueta(null);
  }, [externos, numero, etiqueta]);
  const etiquetaDo = (id: number) => (etiqueta && Number(etiqueta.split(":")[0]) === id ? etiqueta.split(":")[1]! : null);
  // Com uma etiqueta escolhida, só o número dela.
  const doNumero = etiqueta ? Number(etiqueta.split(":")[0]) : numero;
  const visiveis = useMemo(() => (doNumero == null ? externos : externos.filter((e) => e.id === doNumero)), [externos, doNumero]);

  const etiquetas = useQueries({
    queries: externos.map((e) => ({
      queryKey: chaveDasEtiquetas(e.id),
      queryFn: () => customFetch<EtiquetaExterna[]>(`${BASE}/${e.id}/etiquetas`),
      refetchInterval: 30_000,
      retry: false,
    })),
  });
  // Etiqueta apagada no WhatsApp enquanto estava escolhida: o filtro sai junto.
  useEffect(() => {
    if (!etiqueta) return;
    const i = externos.findIndex((e) => e.id === Number(etiqueta.split(":")[0]));
    const l = etiquetas[i]?.data;
    if (l && !l.some((x) => x.id === etiqueta.split(":")[1])) setEtiqueta(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [etiqueta, externos, ...etiquetas.map((q) => q.data)]);

  const consultas = useQueries({
    queries: visiveis.map((e) => {
      const f: Filtros = { busca, naoLidas, etiqueta: etiquetaDo(e.id), limite };
      return {
        queryKey: ["central-externo-conversas", e.id, f] as const,
        queryFn: () => carregarConversas(e.id, f),
        refetchInterval: 10_000,
        retry: false,
        // Carregando mais (só o limite mudou), a lista fica na tela.
        placeholderData: (antes: Pagina | undefined, q: { queryKey: readonly unknown[] } | undefined) => {
          const g = q?.queryKey[2] as Filtros | undefined;
          return g && g.busca === f.busca && g.naoLidas === f.naoLidas && g.etiqueta === f.etiqueta ? antes : undefined;
        },
      };
    }),
  });
  const carregando = consultas.some((q) => q.isLoading);
  const falhas = visiveis.filter((_, i) => consultas[i]?.isError && !consultas[i]?.data);

  // O que se fez aqui e a lista ainda não mostra: marcada como não lida, fixada ou desafixada; no «Não lidas», as que
  // foram abertas continuam na lista (já lidas) até desligar o filtro, como na Central.
  const [marcadas, setMarcadas] = useState<Map<string, number>>(() => new Map());
  const [fixadasAqui, setFixadasAqui] = useState<Map<string, { fixa: boolean; em: number }>>(() => new Map());
  const [ficam, setFicam] = useState<Map<string, ConversaExterna>>(() => new Map());
  useEffect(() => setFicam(new Map()), [naoLidas, numero, etiqueta]);
  useEffect(() => {
    if (naoLidas && aberta) setFicam((f) => (f.has(chaveDe(aberta)) ? f : new Map(f).set(chaveDe(aberta), aberta)));
  }, [naoLidas, aberta]);

  const todas = useMemo(() => {
    const l = new Map<string, ConversaExterna>();
    // Com mais de um número, a conversa mais antiga carregada de cada um que ainda tem mais é o corte: abaixo dele, a ordem
    // só fica certa quando a próxima página daquele número chegar.
    let corte = "";
    visiveis.forEach((_, i) => {
      const d = consultas[i]?.data;
      if (!d) return;
      for (const c of d.conversas) if (!l.has(chaveDe(c))) l.set(chaveDe(c), c);
      if (d.proximo != null && visiveis.length > 1) {
        const mais = d.conversas.filter((c) => !c.fixada).map((c) => c.ultima.em ?? "").sort()[0] ?? "";
        if (mais > corte) corte = mais;
      }
    });
    return { lista: [...l.values()], corte };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visiveis, ...consultas.map((q) => q.data)]);

  useEffect(() => {
    const agora = Date.now();
    setMarcadas((ms) => {
      const ficamM = [...ms].filter(([k, em]) => agora - em < MARCADA_MAX_MS && !todas.lista.some((c) => chaveDe(c) === k && c.naoLidas !== 0));
      return ficamM.length === ms.size ? ms : new Map(ficamM);
    });
    setFixadasAqui((fs) => {
      const ficamF = [...fs].filter(([k, x]) => agora - x.em < MARCADA_MAX_MS && !todas.lista.some((c) => chaveDe(c) === k && c.fixada === x.fixa));
      return ficamF.length === fs.size ? fs : new Map(ficamF);
    });
  }, [todas]);

  // A aberta acompanha a lista.
  useEffect(() => {
    if (!aberta) return;
    const x = todas.lista.find((c) => chaveDe(c) === chaveDe(aberta));
    if (
      x &&
      (x.naoLidas !== aberta.naoLidas ||
        x.fixada !== aberta.fixada ||
        x.nome !== aberta.nome ||
        x.imagem !== aberta.imagem ||
        x.etiquetas.join() !== aberta.etiquetas.join() ||
        x.ultima.em !== aberta.ultima.em)
    )
      onAtualizar(x);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [todas]);

  const exibidas = useMemo(() => {
    const fixa = (c: ConversaExterna) => fixadasAqui.get(chaveDe(c))?.fixa ?? c.fixada;
    const l = new Map(todas.lista.filter((c) => fixa(c) || !todas.corte || (c.ultima.em ?? "") >= todas.corte).map((c) => [chaveDe(c), c]));
    if (naoLidas) for (const [k, c] of ficam) if (!l.has(k) && visiveis.some((e) => e.id === c.externo)) l.set(k, { ...c, naoLidas: 0 });
    const desc = (a: ConversaExterna, b: ConversaExterna) => String(b.ultima.em ?? "").localeCompare(String(a.ultima.em ?? ""));
    const lista = [...l.values()].map((c) => ({
      ...c,
      fixada: fixa(c),
      naoLidas: aberta && chaveDe(aberta) === chaveDe(c) ? 0 : marcadas.has(chaveDe(c)) && c.naoLidas === 0 ? 1 : c.naoLidas,
    }));
    return [...lista.filter((c) => c.fixada).sort(desc), ...lista.filter((c) => !c.fixada).sort(desc)];
  }, [todas, fixadasAqui, naoLidas, ficam, visiveis, aberta, marcadas]);

  // O contador do «Não lidas»: o do número (ou da etiqueta), somado.
  const totalNaoLidas = visiveis.reduce<number | null>((s, _, i) => {
    const n = consultas[i]?.data?.naoLidasTotal;
    return typeof n === "number" ? (s ?? 0) + n : s;
  }, null);

  // Rolagem infinita: o fim da lista à vista pede mais (nos números que ainda têm).
  const temMais = visiveis.some((_, i) => consultas[i]?.data?.proximo != null);
  const buscandoMais = consultas.some((q) => q.isFetching && q.isPlaceholderData);
  const fim = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = fim.current;
    if (!el || !temMais || buscandoMais) return;
    const io = new IntersectionObserver((e) => {
      if (e.some((x) => x.isIntersecting)) setLimite((l) => l + POR_VEZ);
    });
    io.observe(el);
    return () => io.disconnect();
  }, [temMais, buscandoMais, exibidas.length]);

  const reler = (id: number) => void qc.invalidateQueries({ queryKey: ["central-externo-conversas", id] });
  const fixar = async (c: ConversaExterna, fixa: boolean) => {
    try {
      await customFetch(`${BASE}/${c.externo}/fixar`, { method: "POST", body: JSON.stringify({ jid: c.chatid, fixar: fixa }) });
      setFixadasAqui((fs) => new Map(fs).set(chaveDe(c), { fixa, em: Date.now() }));
      reler(c.externo);
    } catch (err) {
      toast({ title: extractApiErrorMessage(err, fixa ? "Não foi possível fixar a conversa." : "Não foi possível desafixar a conversa."), variant: "destructive" });
    }
  };
  const marcarNaoLida = async (c: ConversaExterna) => {
    try {
      await customFetch(`${BASE}/${c.externo}/nao-lida`, { method: "POST", body: JSON.stringify({ jid: c.chatid }) });
      setMarcadas((ms) => new Map(ms).set(chaveDe(c), Date.now()));
      if (aberta && chaveDe(aberta) === chaveDe(c)) onAbrir(null);
      reler(c.externo);
    } catch (err) {
      toast({ title: extractApiErrorMessage(err, "Não foi possível marcar como não lida."), variant: "destructive" });
    }
  };

  const chip = (ativo: boolean) =>
    `shrink-0 whitespace-nowrap rounded-full border px-2.5 py-0.5 font-medium ${ativo ? "border-violet-600 bg-violet-600 text-white" : "bg-background text-muted-foreground hover:bg-muted"}`;
  const etiquetasVisiveis = visiveis.flatMap((e) => (etiquetas[externos.indexOf(e)]?.data ?? []).map((x) => ({ ...x, externo: e.id, chave: `${e.id}:${x.id}` })));
  const filtrando = !!busca || naoLidas || !!etiqueta || numero != null;

  return (
    <>
      <div className="space-y-2.5 border-b bg-violet-50/70 p-3 dark:bg-violet-950/30" data-testid="atendimento-pessoais">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-violet-700 dark:text-violet-300">WhatsApps pessoais · fora do Venda no Zap</p>
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            value={buscaDigitada}
            onChange={(e) => setBuscaDigitada(e.target.value)}
            placeholder="Buscar por nome ou telefone"
            inputMode="search"
            className={`h-9 w-full rounded-lg border bg-background pl-8 text-sm outline-none focus:ring-2 focus:ring-violet-400 ${buscaDigitada ? "pr-9" : "pr-3"}`}
            data-testid="atendimento-pessoais-busca"
          />
          {buscaDigitada && (
            <button
              type="button"
              onClick={() => setBuscaDigitada("")}
              aria-label="Limpar a busca"
              title="Limpar a busca"
              className="absolute right-1.5 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
              data-testid="atendimento-pessoais-busca-limpar"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
        {etiquetasVisiveis.length > 0 && (
          <div className="-mx-3 flex gap-1.5 overflow-x-auto px-3 pb-1 text-xs hover-scrollbar" role="group" aria-label="Filtrar por etiqueta" data-testid="atendimento-pessoais-etiquetas">
            {etiquetasVisiveis.map((x) => (
              <button
                key={x.chave}
                type="button"
                onClick={() => setEtiqueta(etiqueta === x.chave ? null : x.chave)}
                aria-pressed={etiqueta === x.chave}
                className={`flex items-center gap-1.5 ${chip(etiqueta === x.chave)}`}
                data-testid={`atendimento-pessoais-etiqueta-${x.chave}`}
              >
                <span className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: x.cor ?? "currentColor" }} />
                {x.nome}
                {doNumero == null && externos.length > 1 && <span className="opacity-60">· {rotuloCurto(externos.find((e) => e.id === x.externo)?.rotulo ?? "")}</span>}
                <span className="opacity-70">{x.conversas}</span>
              </button>
            ))}
          </div>
        )}
        <div className="flex flex-wrap gap-1.5 text-xs" role="group" aria-label="Filtrar conversas">
          {[{ id: null as number | null, rotulo: "Todos" }, ...externos.map((e) => ({ id: e.id as number | null, rotulo: e.rotulo }))].map((x) => (
            <button
              key={String(x.id)}
              type="button"
              onClick={() => {
                setNumero(x.id);
                if (x.id == null || (etiqueta && Number(etiqueta.split(":")[0]) !== x.id)) setEtiqueta(null);
              }}
              aria-pressed={doNumero === x.id}
              className={chip(doNumero === x.id)}
              data-testid={`atendimento-pessoais-filtro-${x.id ?? "todos"}`}
            >
              {x.rotulo}
            </button>
          ))}
          <button type="button" onClick={() => setNaoLidas((v) => !v)} aria-pressed={naoLidas} className={`flex items-center gap-1.5 ${chip(naoLidas)}`} data-testid="atendimento-pessoais-nao-lidas">
            Não lidas
            {totalNaoLidas !== null && <span className="opacity-70">{totalNaoLidas + marcadas.size}</span>}
          </button>
        </div>
        {externos.some((e) => !e.conectado) && (
          <p className="text-xs text-destructive">Desconectado: {externos.filter((e) => !e.conectado).map((e) => e.rotulo).join(", ")}.</p>
        )}
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto" data-testid="atendimento-pessoais-lista">
        {falhas.length > 0 && <p className="px-4 pt-3 text-xs text-destructive">Não deu para ler: {falhas.map((e) => e.rotulo).join(", ")}. Tento de novo em instantes.</p>}
        {carregando && exibidas.length === 0 ? (
          <p className="p-4 text-sm text-muted-foreground">Carregando…</p>
        ) : exibidas.length === 0 ? (
          <p className="p-6 text-center text-sm text-muted-foreground">{filtrando ? "Nada com esse filtro." : "Nenhuma conversa."}</p>
        ) : (
          exibidas.map((c) => (
            <ItemPessoal
              key={chaveDe(c)}
              c={c}
              externos={externos}
              ativa={!!aberta && chaveDe(aberta) === chaveDe(c)}
              rascunho={aberta && chaveDe(aberta) === chaveDe(c) ? "" : rascunhoDe(c)}
              onClick={() => onAbrir(c)}
              onFixar={(fixa) => void fixar(c, fixa)}
              onNaoLida={() => void marcarNaoLida(c)}
            />
          ))
        )}
        <div ref={fim} className="h-6">
          {buscandoMais && <Loader2 className="mx-auto h-4 w-4 animate-spin text-muted-foreground" />}
        </div>
      </div>
    </>
  );
}

// O card, como o da Central: a foto do contato, a etiqueta do número, o rascunho e a setinha com «Fixar conversa» e, na
// já lida, «Marcar como não lida».
function ItemPessoal({
  c,
  externos,
  ativa,
  rascunho,
  onClick,
  onFixar,
  onNaoLida,
}: {
  c: ConversaExterna;
  externos: Externo[];
  ativa: boolean;
  rascunho: string;
  onClick: () => void;
  onFixar: (fixa: boolean) => void;
  onNaoLida: () => void;
}) {
  const ext = externos.find((e) => e.id === c.externo);
  return (
    <div className="group relative">
      <button
        type="button"
        onClick={onClick}
        className={`flex w-full items-center gap-3 border-b px-3 py-2.5 text-left hover:bg-muted/50 ${ativa ? "bg-violet-50 dark:bg-violet-950/40" : ""}`}
        data-testid={`atendimento-pessoal-${c.externo}-${c.chatid}`}
      >
        {c.imagem ? (
          <img src={c.imagem} alt="" className="h-10 w-10 shrink-0 rounded-full object-cover" />
        ) : (
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-muted text-sm font-semibold text-muted-foreground">
            {c.grupo ? <Users className="h-4 w-4" /> : (c.nome.trim()[0] ?? "?").toUpperCase()}
          </span>
        )}
        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-1.5">
            <span className="truncate text-sm font-semibold">{c.nome}</span>
            {ext && <span className={`shrink-0 rounded px-1.5 py-px text-[10px] font-bold ${corDo(externos, c.externo)}`}>{rotuloCurto(ext.rotulo)}</span>}
            {c.fixada && <Pin className="ml-auto h-3 w-3 shrink-0 rotate-45 text-muted-foreground" aria-label="Fixada" data-testid="atendimento-pessoal-fixada" />}
            <span className={`${c.fixada ? "" : "ml-auto "}shrink-0 pl-1 text-[11px] text-muted-foreground`}>{hora(c.ultima.em)}</span>
          </span>
          <span className="flex items-center gap-2 group-hover:pr-7 [@media(hover:none)]:pr-7">
            {rascunho ? (
              <span className="min-w-0 flex-1 truncate text-xs text-green-700 dark:text-green-400" data-testid="atendimento-pessoal-rascunho">
                <span className="font-semibold">Rascunho:</span> {rascunho.replace(/\s+/g, " ")}
              </span>
            ) : (
              <span className="min-w-0 flex-1 truncate text-xs text-muted-foreground">{c.ultima.texto || rotuloDoTipo(c.ultima.tipo) || ""}</span>
            )}
            {c.naoLidas !== 0 && (
              <span className="ml-auto flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full bg-violet-600 px-1.5 text-[11px] font-bold text-white" data-testid="atendimento-pessoal-nao-lidas">
                {c.naoLidas > 0 ? c.naoLidas : ""}
              </span>
            )}
          </span>
        </span>
      </button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            title="Mais opções"
            aria-label="Mais opções da conversa"
            className="absolute bottom-2 right-2 flex h-6 w-6 items-center justify-center rounded-md border bg-background text-muted-foreground opacity-0 shadow-sm hover:text-foreground focus-visible:opacity-100 group-hover:opacity-100 data-[state=open]:opacity-100 [@media(hover:none)]:opacity-100"
            data-testid={`atendimento-pessoal-menu-${c.externo}-${c.chatid}`}
          >
            <ChevronDown className="h-4 w-4" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" collisionPadding={16}>
          <DropdownMenuItem onSelect={() => onFixar(!c.fixada)} data-testid="atendimento-pessoal-fixar">
            {c.fixada ? "Desafixar conversa" : "Fixar conversa"}
          </DropdownMenuItem>
          {c.naoLidas === 0 && (
            <DropdownMenuItem onSelect={onNaoLida} data-testid="atendimento-pessoal-marcar-nao-lida">
              Marcar como não lida
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
